// src/components/EggFoundCelebration.tsx
import { useCallback, useEffect, useState } from 'react'
import { motion } from 'framer-motion'
import ReactCanvasConfetti from 'react-canvas-confetti'
import { BunnyCharacter } from './BunnyCharacter'
import { Button } from './ui/Button'

interface EggFoundCelebrationProps {
  eggNumber: number
  totalEggs: number
  onContinue: () => void
}

const CELEBRATION_PHRASES = [
  '🎉 ÄGG HITTAT!',
  '🥕 KANINEN JUBLAR!',
  '⭐ FANTASTISKT!',
  '🌟 DU ÄR BÄST!',
  '🎊 HURRA HURRA!',
  '🐣 MÄSTERLIG JÄGARE!',
]

export function EggFoundCelebration({ eggNumber, totalEggs, onContinue }: EggFoundCelebrationProps) {
  const isLast = eggNumber === totalEggs
  const [phrase] = useState(() =>
    isLast ? '🏆 SUPERÄGGET HITTAT!' : CELEBRATION_PHRASES[Math.floor(Math.random() * CELEBRATION_PHRASES.length)]
  )
  const [showButton, setShowButton] = useState(false)

  useEffect(() => {
    const t = setTimeout(() => setShowButton(true), 2000)
    return () => clearTimeout(t)
  }, [])

  const confettiCallback = useCallback((confetti: any) => {
    if (!confetti) return
    confetti({
      particleCount: isLast ? 300 : 120,
      spread: isLast ? 180 : 80,
      origin: { y: 0.5 },
      colors: ['#FF6B6B', '#4ECDC4', '#FFE66D', '#A8E6CF', '#DDA0DD'],
    })
    if (isLast) {
      setTimeout(() => confetti({
        particleCount: 150,
        spread: 120,
        origin: { y: 0.3, x: 0.2 },
        colors: ['#FFD700', '#FF6B6B', '#4ECDC4'],
      }), 600)
      setTimeout(() => confetti({
        particleCount: 150,
        spread: 120,
        origin: { y: 0.3, x: 0.8 },
        colors: ['#FFD700', '#FF6B6B', '#4ECDC4'],
      }), 900)
    }
  }, [isLast])

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 bg-cream/97 z-50 flex flex-col items-center justify-center p-6"
    >
      <ReactCanvasConfetti
        onInit={({ confetti }) => confettiCallback(confetti)}
        style={{ position: 'fixed', pointerEvents: 'none', width: '100%', height: '100%', top: 0, left: 0, zIndex: 51 }}
      />

      <motion.div
        initial={{ scale: 0 }}
        animate={{ scale: 1 }}
        transition={{ type: 'spring', stiffness: 300, delay: 0.1 }}
      >
        <BunnyCharacter size="lg" animate={isLast ? 'celebrate' : 'bounce'} />
      </motion.div>

      <motion.p
        initial={{ y: 30, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ delay: 0.4 }}
        className={`font-heading text-center mt-6 ${isLast ? 'text-4xl text-coral' : 'text-3xl text-turquoise'}`}
      >
        {phrase}
      </motion.p>

      <motion.p
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.8 }}
        className="font-heading text-2xl text-lavender mt-3"
      >
        {eggNumber} av {totalEggs}
      </motion.p>

      {showButton && (
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          className="mt-8 w-full max-w-xs"
        >
          <Button size="lg" onClick={onContinue} className="w-full">
            {isLast ? 'Avsluta äventyret! 🎉' : 'Nästa ledtråd →'}
          </Button>
        </motion.div>
      )}
    </motion.div>
  )
}
